import { defineStore } from 'pinia'
import { mande, type MandeError } from 'mande'

import type { SupportCategory } from '@/api/useSupport'

interface SupportEnvelope<T> {
  success: boolean
  data: T
}

interface SupportState {
  categories: SupportCategory[] | null
  loading: boolean
  error: MandeError | null
  fetched: boolean
  cacheKey: string
}

let inflight: Promise<void> | null = null

function currentLanguage(): string {
  const nuxtApp = useNuxtApp()
  const locale = (nuxtApp.$i18n as any)?.locale?.value || 'tr'
  return String(locale).split('-')[0] || 'tr'
}

export const useSupportStore = defineStore('support', {
  state: (): SupportState => ({
    categories: null,
    loading: false,
    error: null,
    fetched: false,
    cacheKey: '',
  }),

  getters: {
    isLoaded: (state) => state.fetched && state.categories !== null,

    // Eski useCategoriesStore.rootCategoriesWithSubItems'ın döndürdüğü tree
    // ile uyumlu shape: [{ id, title, slug, image_url, items: [{ id, title, slug, url }] }]
    legacyTree: (state) =>
      (state.categories || []).map((c) => ({
        id: String(c.id),
        title: c.name,
        slug: c.slug,
        image_url: c.icon_url || undefined,
        items: [
          ...c.articles.map((a) => ({
            id: String(a.id),
            title: a.title,
            slug: a.slug,
            url: `${c.slug}/${a.slug}`,
          })),
          ...c.subcategories.map((sc) => ({
            id: String(sc.id),
            title: sc.name,
            slug: sc.slug,
            url: `${c.slug}/${sc.slug}`,
          })),
        ],
      })),

    // Eski useCategoriesStore.rootCategories ile uyumlu flat root listesi
    rootList: (state) =>
      (state.categories || []).map((c) => ({
        id: String(c.id),
        title: c.name,
        slug: c.slug,
        image_url: c.icon_url || undefined,
      })),
  },

  actions: {
    fetchSupportCategories(force = false, langOverride?: string): Promise<void> {
      const language = langOverride ?? currentLanguage()
      if (this.cacheKey !== language) force = true
      if (this.fetched && !force) return Promise.resolve()
      if (inflight) return inflight

      this.loading = true
      this.error = null

      inflight = (async () => {
        try {
          const config = useRuntimeConfig()
          const api = mande(`${config.public.apiBaseUrl}/api/v1/customer/support`)
          const response = await api.get<SupportEnvelope<{ categories: SupportCategory[] }>>(
            'categories',
            { query: { lang: language } },
          )
          this.categories = response.data?.categories ?? []
          this.fetched = true
          this.cacheKey = language
        } catch (err) {
          this.error = err as MandeError
          this.categories = []
        } finally {
          this.loading = false
          inflight = null
        }
      })()

      return inflight
    },

    resetCache() {
      this.fetched = false
      this.categories = null
      this.cacheKey = ''
      inflight = null
    },
  },
})
